// 帳號權限：Admin／子帳號。規格見 docs/account-admin-subaccount-spec.md
import { FetchError } from "@/utils/error";

export type TAccountPermission =
  | "access_estimate"
  | "view_estimate_cost"
  | "edit_knowledge_base"
  | "delete_project";

export type TAccess = {
  profile: "admin" | "member";
  /** 設過 Admin PIN 才是 true；false 時一律是 admin */
  sub_account_enabled: boolean;
  sub_account: { id: number; name: string } | null;
  permissions: Record<TAccountPermission | "manage_sub_account", boolean>;
  pin_locked_until: string | null;
  /** 從忘記密碼信進來的 session：改密碼不用舊密碼 */
  password_reset_session: boolean;
};

export type TSubAccount = {
  id: number;
  name: string;
  permissions: Record<TAccountPermission, boolean>;
  is_active: boolean;
  pin_set_at: string | null;
  created_at: string;
};

export type TVerificationCodeSent = {
  sent_to: string;
  resend_after: number;
};

/** 帳號權限 API 的錯誤：保留後端的 code 和額外欄位（remaining_attempts、locked_until…） */
export class AccessError extends FetchError {
  code: string;
  data: Record<string, unknown>;

  constructor(res: Response, data: Record<string, unknown>) {
    super(res, (data.message as string) || (data.msg as string));
    this.code = (data.code as string) || "";
    this.data = data;
  }
}

/** 忘記密碼信的連結把 token 放在網址上，還沒寫進 localStorage 前也要能用 */
export const currentToken = () =>
  localStorage.getItem("site") ||
  new URL(window.location.href).searchParams.get("token") ||
  "";

const request = async <T>(
  path: string,
  method: "GET" | "POST" | "PATCH" | "DELETE" = "GET",
  body?: unknown,
): Promise<T> => {
  const response = await fetch(
    `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/${path}`,
    {
      method,
      headers: {
        Authorization: `Bearer ${currentToken()}`,
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    },
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new AccessError(response, data ?? {});
  }
  return data as T;
};

/** 有些操作會順便換發這台的 token（原本用舊共用 token 的裝置） */
const adoptToken = <T extends { token?: string }>(data: T): T => {
  if (data.token) localStorage.setItem("site", data.token);
  return data;
};

export const AccessAPI = {
  getAccess: () => request<TAccess>("access/"),
  enterAdmin: (pin: string) =>
    request<TAccess>("access/profile/", "POST", { profile: "admin", pin }),
  leaveAdmin: () =>
    request<TAccess>("access/profile/", "POST", { profile: "member" }),

  setupPin: async (password: string, pin: string) =>
    adoptToken(
      await request<{
        sub_account: TSubAccount;
        access: TAccess;
        token?: string;
      }>("pin/setup/", "POST", { password, pin }),
    ),
  changePin: (currentPin: string, newPin: string) =>
    request<{ success: boolean }>("pin/change/", "POST", {
      current_pin: currentPin,
      new_pin: newPin,
    }),
  sendPinResetCode: () =>
    request<TVerificationCodeSent>("pin/reset/verification_code/", "POST", {}),
  resetPin: (code: string, newPin: string) =>
    request<TAccess>("pin/reset/", "POST", { code, new_pin: newPin }),

  getSubAccount: () => request<TSubAccount | null>("sub_account/"),
  updateSubAccount: (data: {
    name?: string;
    permissions?: Partial<Record<TAccountPermission, boolean>>;
  }) => request<TSubAccount>("sub_account/", "PATCH", data),
  disableSubAccount: async () =>
    adoptToken(
      await request<{ access: TAccess; token?: string }>(
        "sub_account/",
        "DELETE",
      ),
    ),
  logoutAll: async () =>
    adoptToken(
      await request<{ success: boolean; token?: string }>(
        "logout_all/",
        "POST",
        {},
      ),
    ),
  logout: () => request<unknown>("logout/", "POST", {}),
};

export const ACCESS_QUERY_KEY = ["account-access"] as const;
